import { format } from "date-fns";

import {
  FilteredResolvedBlockchainBeneficiary,
  ResolvedStatement,
} from "@generated/types";

import { CompanyConfig } from "./companyConfig";
import { formatPdfDisplayDate } from "./formatPdfDisplayDate";
import { getBlockchainBeneficiaryName } from "./getLegalEntityName";
import { sortStatementEntries } from "./sortStatementEntries";
import { isPooledStatementAccount } from "./statementAccounts";
import {
  getTransactionTypeLabel,
  TRANSACTION_FEE_LABEL,
} from "./statementTransactionLabels";
import {
  BaseTransactionEntry,
  CurrenciesStatementPDFData,
  DigitalAssetsStatementPDFData,
  StatementBankAccount,
  StatementBeneficialOwner,
} from "./statementTypes";

// Re-export types for backward compatibility
export type { BaseTransactionEntry as TransactionEntry } from "./statementTypes";

// Type for resolved data to improve type safety
interface ResolvedEntryData {
  beneficiary?:
    | {
        name?: string;
        nickname?: string;
        bankAccount?: {
          accountName?: string;
        };
        wallet?: {
          address?: string;
        };
      }
    | FilteredResolvedBlockchainBeneficiary;
  remittanceInformation?: string;
  /**
   * Drives getTransactionTypeLabel's CANCELLED override, so the label this file
   * derives matches the one the templates render in the TYPE column.
   */
  paymentState?: string;
  resolvedRelatedPayment?: {
    beneficiary?: {
      name?: string;
      bankAccount?: {
        accountName?: string;
      };
    };
    remittanceInformation?: string;
  };
  debitBankAccount?: {
    accountName?: string;
  };
  narrative?: string[];
}

// Entry type from the API
interface LedgerEntry {
  journal: string;
  reason?: string;
  comment?: string;
  credit: number;
  debit: number;
  balance?: number;
  bookDate: string;
  uuid?: string;
  item?: number;
  resolvedData?: ResolvedEntryData;
}

/**
 * A row's counterparty name and its reference, kept apart so the template can
 * stack them on two lines. The alternative — keep flattening to "Name (REF)"
 * here and split it apart in the template — is lossy: any name legitimately
 * containing brackets would be mis-split. The template re-adds the brackets
 * around the reference itself.
 */
interface EntryDescription {
  description: string;
  reference?: string;
}

// Description builder types
type DescriptionBuilder = (
  entry: LedgerEntry,
  resolvedData?: ResolvedEntryData
) => EntryDescription;

// Declarative description builders for each transaction type
const descriptionBuilders: Record<string, DescriptionBuilder> = {
  // Fiat payment descriptions
  "payments:*": (entry, resolvedData) => {
    const beneficiary = resolvedData?.beneficiary;
    const beneficiaryName =
      beneficiary && "name" in beneficiary
        ? (beneficiary.name as string | undefined) ||
          (beneficiary.bankAccount as { accountName?: string })?.accountName
        : undefined;
    const remittanceInfo = resolvedData?.remittanceInformation;

    return nameWithReference(
      beneficiaryName,
      remittanceInfo,
      entry.comment || "Payment"
    );
  },

  // Bank transaction descriptions
  "bank-transactions:debit": (entry, resolvedData) => {
    const payment = resolvedData?.resolvedRelatedPayment;
    const accountName =
      payment?.beneficiary?.name ||
      payment?.beneficiary?.bankAccount?.accountName;
    const remittanceInfo = payment?.remittanceInformation;

    return nameWithReference(
      accountName,
      remittanceInfo,
      entry.comment || "Payment"
    );
  },

  "bank-transactions:credit": (entry, resolvedData) => {
    const accountName =
      resolvedData?.debitBankAccount?.accountName || entry?.comment;
    const remittanceInfo = resolvedData?.narrative?.[0];

    return nameWithReference(accountName, remittanceInfo, "Deposit");
  },

  // FX ticket descriptions
  "fx-tickets:settlement": (entry) => {
    return {
      description: entry.comment || entry.reason || "Currency exchange",
    };
  },

  // Blockchain transaction descriptions
  "blockchain-transactions:credit": (entry) => {
    return { description: entry.comment || "Deposit received" };
  },

  "blockchain-transactions:debit": (entry, resolvedData) => {
    return {
      description: formatBlockchainBeneficiary(
        resolvedData?.beneficiary,
        entry.comment || "Payment"
      ),
    };
  },

  "blockchain-transactions:credit-deduction": (entry) => {
    return { description: entry.comment || "Transaction fee" };
  },

  // Blockchain withdrawal descriptions
  "blockchain-withdrawals:*": (entry, resolvedData) => {
    return {
      description: formatBlockchainBeneficiary(
        resolvedData?.beneficiary,
        entry.comment || "Withdrawal"
      ),
    };
  },
};

/**
 * Keep the name and the reference apart instead of flattening them into
 * "Name (REF)". When there is no name the fallback label holds line 1 and the
 * reference still gets its own line, rather than the reference silently
 * standing in as the description.
 */
function nameWithReference(
  name?: string,
  remittanceInfo?: string,
  fallback: string = ""
): EntryDescription {
  return {
    description: name || fallback,
    reference: remittanceInfo || undefined,
  };
}

// Helper function to format blockchain beneficiary
function formatBlockchainBeneficiary(
  beneficiary?: ResolvedEntryData["beneficiary"],
  fallback: string = ""
): string {
  if (!beneficiary) return fallback;

  // Check if it's a blockchain beneficiary with legal entity
  if ("legalEntity" in beneficiary && beneficiary.legalEntity) {
    const name = getBlockchainBeneficiaryName(
      beneficiary as FilteredResolvedBlockchainBeneficiary
    );
    if (name && String(name).trim() && name !== "undefined undefined") {
      return String(name);
    }
  }

  // Fallback to nickname
  if (beneficiary.nickname) {
    return String(beneficiary.nickname);
  }

  // Fallback to truncated wallet address
  if ("wallet" in beneficiary && beneficiary.wallet?.address) {
    const addr = beneficiary.wallet.address;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }

  return fallback;
}

// Get description for an entry using the builders
function getDescription(
  entry: LedgerEntry,
  resolvedData?: ResolvedEntryData
): EntryDescription {
  // A fee is a charge from us, not a transaction with the payment's recipient.
  // Without this guard fee entries fall through to their journal's wildcard
  // builder and the PDF names the beneficiary on the fee line — the same
  // misattribution the CSV guard removes in getCounterParty. Derived from the
  // label map so the fee taxonomy stays defined in exactly one place.
  if (
    getTransactionTypeLabel(entry.journal, entry.reason, resolvedData) ===
    TRANSACTION_FEE_LABEL
  ) {
    return {
      description: entry.comment || TRANSACTION_FEE_LABEL,
      // The reference still ties the fee back to its parent payment without
      // naming the recipient, matching the CSV's Reference column.
      reference: resolvedData?.remittanceInformation || undefined,
    };
  }

  const key = `${entry.journal}:${entry.reason}`;
  const wildcardKey = `${entry.journal}:*`;

  const builder = descriptionBuilders[key] || descriptionBuilders[wildcardKey];

  if (builder) {
    return builder(entry, resolvedData);
  }

  // Fallback for unknown transaction types
  return {
    description:
      entry.comment || entry.reason || `${entry.journal} transaction`,
  };
}

// Common transformation logic
function transformEntries(entries: LedgerEntry[]): {
  transformedEntries: BaseTransactionEntry[];
  totalMoneyIn: number;
  totalMoneyOut: number;
} {
  let totalMoneyIn = 0;
  let totalMoneyOut = 0;

  // Filter out entries with comments starting with "IAT"
  entries = entries.filter((entry) => !entry.comment?.startsWith("IAT"));

  entries = sortStatementEntries(entries);

  const transformedEntries = entries.map((entry) => {
    const isCredit = entry.credit > 0;
    const amount = isCredit ? entry.credit : entry.debit;

    if (isCredit) {
      totalMoneyIn += amount;
    } else {
      totalMoneyOut += amount;
    }

    const resolvedData = entry.resolvedData as ResolvedEntryData;
    const { description, reference } = getDescription(entry, resolvedData);

    return {
      date: format(new Date(entry.bookDate), "dd/MM/yyyy"),
      type: entry.journal,
      description,
      reference,
      moneyOut: !isCredit ? amount : null,
      moneyIn: isCredit ? amount : null,
      accountBalance: entry.balance || 0,
      journal: entry.journal,
      reason: entry.reason,
      resolvedData: resolvedData,
    };
  });

  return { transformedEntries, totalMoneyIn, totalMoneyOut };
}

// Common base data for both statement types
function createBaseStatementData(
  statement: ResolvedStatement,
  customerInfo: {
    name: string;
    address: string[];
    beneficialOwner?: StatementBeneficialOwner;
  },
  companyConfig: CompanyConfig,
  totals: { totalMoneyIn: number; totalMoneyOut: number }
) {
  return {
    // Company information from config
    companyName: companyConfig.name,
    companyAddress: companyConfig.address,

    // Customer information
    customerName: customerInfo.name,
    customerAddress: customerInfo.address,
    beneficialOwner: customerInfo.beneficialOwner,

    // Statement period + generated date — word form (Aug 29th, 2026).
    // Transaction row dates stay dd/MM/yyyy for the compact table.
    statementPeriod: {
      from: formatPdfDisplayDate(new Date(statement.startDate)),
      to: formatPdfDisplayDate(new Date(statement.endDate)),
    },
    generatedOn: formatPdfDisplayDate(new Date()),

    // Balance summary
    balanceSummary: {
      openingBalance: statement.openingBalance,
      moneyOut: totals.totalMoneyOut,
      moneyIn: totals.totalMoneyIn,
      closingBalance: statement.closingBalance,
    },

    // Currency
    currency: statement.product,

    // Footer text
    footerText: companyConfig.footerText,
  };
}

// Transform currencies (fiat) statement data
export function transformCurrenciesStatementData(
  statement: ResolvedStatement,
  customerInfo: {
    name: string;
    address: string[];
    beneficialOwner?: StatementBeneficialOwner;
    bankAccounts: StatementBankAccount[];
  },
  companyConfig: CompanyConfig
): CurrenciesStatementPDFData {
  const { transformedEntries, totalMoneyIn, totalMoneyOut } = transformEntries(
    (statement.entries || []) as LedgerEntry[]
  );

  return {
    accountType: "currencies",
    ...createBaseStatementData(statement, customerInfo, companyConfig, {
      totalMoneyIn,
      totalMoneyOut,
    }),

    // Bank account information — every account for the currency, the
    // customer's own first so a pooled routing account never leads the block.
    // Copy before sorting so this stays a pure transform: callers must be able
    // to pass an array they still own without it being reordered underneath
    // them. (Today's caller happens to pass a fresh flatMap result.)
    bankAccounts: [...customerInfo.bankAccounts].sort(
      (a, b) =>
        Number(isPooledStatementAccount(a)) -
        Number(isPooledStatementAccount(b))
    ),

    // Transaction entries
    entries: transformedEntries,
  };
}

// Transform digital assets (blockchain) statement data
export function transformDigitalAssetsStatementData(
  statement: ResolvedStatement,
  customerInfo: {
    name: string;
    address: string[];
    beneficialOwner?: StatementBeneficialOwner;
    product: string;
    accountName: string;
  },
  companyConfig: CompanyConfig
): DigitalAssetsStatementPDFData {
  const { transformedEntries, totalMoneyIn, totalMoneyOut } = transformEntries(
    (statement.entries || []) as LedgerEntry[]
  );

  return {
    accountType: "digital-assets",
    ...createBaseStatementData(statement, customerInfo, companyConfig, {
      totalMoneyIn,
      totalMoneyOut,
    }),

    // Wallet information
    walletAddress: {
      product: customerInfo.product,
      accountName: customerInfo.accountName,
    },

    // Transaction entries
    entries: transformedEntries,
  };
}
